import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, PageHeader } from '../../components';
import { apiRequest } from '../../lib/api';
import type { ListResponse } from '../../lib/useCrud';
import type { LookupOption } from '../leads/types';
import { ContactFormModal } from './ContactFormModal';
import type { ContactPayload, ContactRow } from './types';

export function ContactDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const contactQ = useQuery({
    queryKey: ['contact', id],
    queryFn: () => apiRequest<{ data: ContactRow }>(`/contacts/${id}`),
    enabled: !!id,
  });
  const categoriesQ = useQuery({
    queryKey: ['lookup', 'contact-categories'],
    queryFn: () =>
      apiRequest<ListResponse<LookupOption>>(
        '/settings/contact-categories?limit=100&include_inactive=false',
      ),
  });

  const updateMut = useMutation({
    mutationFn: (body: ContactPayload) =>
      apiRequest<{ data: ContactRow }>(`/contacts/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact', id] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  if (contactQ.isLoading) return <div className="card card-body muted">Loading…</div>;
  if (contactQ.error) {
    return (
      <div className="alert">
        {contactQ.error instanceof Error ? contactQ.error.message : 'Failed to load'}
      </div>
    );
  }
  const c = contactQ.data?.data;
  if (!c) return null;

  const categories = categoriesQ.data?.data ?? [];
  const categoryName = c.contact_category_id
    ? categories.find((cat) => cat.id === c.contact_category_id)?.name ?? null
    : null;

  return (
    <>
      <PageHeader
        crumb={<Link to="/contacts">Contacts</Link>}
        title={c.full_name ?? 'Unnamed contact'}
        subtitle={c.email ?? c.phone ?? '—'}
        actions={
          <Button icon="edit" onClick={() => setEditing(true)}>
            Edit
          </Button>
        }
      />
      <div className="card">
        <div
          className="card-body"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}
        >
          <Field label="Full name" value={c.full_name} />
          <Field label="First name" value={c.first_name} />
          <Field label="Last name" value={c.last_name} />
          <Field label="Email" value={c.email} />
          <Field label="Phone" value={c.phone} />
          <Field label="City" value={c.city} />
          <Field label="Country" value={c.country} />
          <Field label="Date of birth" value={c.date_of_birth} />
          <Field label="Category" value={categoryName} />
          <Field label="Created" value={formatDate(c.created_at)} />
          <div style={{ gridColumn: '1 / -1' }}>
            <div className="field-label">Notes</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {c.notes || <span className="muted">—</span>}
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <ContactFormModal
          row={c}
          categories={categories}
          onClose={() => setEditing(false)}
          onSubmit={async (payload) => {
            await updateMut.mutateAsync(payload);
            setEditing(false);
          }}
        />
      )}
    </>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="field-label">{label}</div>
      <div>{value || <span className="muted">—</span>}</div>
    </div>
  );
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
